import React, { useState, useRef, ReactNode, useEffect } from "react";
import styles from "./Dropdown.module.scss";
import cn from "classnames";

export interface DropdownProps {
  /**
   * The body of the Dropdown
   */
  readonly children?: ReactNode;

  /**
   * The trigger for the dropdown
   */
  readonly menu?: ReactNode;

  /**
   * The content of the title inside the header
   */
  readonly title?: string;

  /**
   * The id of the button
   */
  readonly id: string;

  /**
   * The disabled state
   */
  readonly disabled?: boolean;

  /**
   * The dropdown classname
   */
  readonly dropdownClassName?: string;

  /**
   * The closed state
   */
  readonly isClosed?: boolean;

  /**
   * onBlur
   */
  readonly onBlur?: () => void;
}

/**
 * @deprecated use `import { MenuButton } from "capitalrx-components-2.0/lib/general"
 */
export const Dropdown: React.FC<DropdownProps> = ({
  id,
  children,
  menu,
  title,
  disabled = false,
  dropdownClassName,
  isClosed = false,
  onBlur = undefined,
}: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isClosed) {
      setOpen(false);
      setIsFocused(false);
    }
  }, [isClosed]);

  const containerClassNames = cn(styles.dropdownContainer, { [styles["disabled"]]: disabled });
  const dropdownClassNames = cn(styles.dropdown, dropdownClassName);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    if (!disabled && !isFocused) {
      setOpen((open) => !open);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    const { relatedTarget } = e;

    if (relatedTarget === dropdownRef.current || (dropdownRef.current && dropdownRef.current.contains(relatedTarget as Node))) {
      return;
    }

    onBlur?.();

    setOpen(false);
    setIsFocused(false);
  };

  const handleFocus = () => {
    setOpen(true);
    setIsFocused(true);
  };

  return (
    <div
      ref={dropdownRef}
      data-testid={`test__dropdownButton-${id}`}
      onClick={handleClick}
      className={containerClassNames}
      onBlur={handleBlur}
      onFocus={handleFocus}
      tabIndex={0}
    >
      {menu}
      <div className={open ? dropdownClassNames : styles.hide}>
        {title && <span className={styles.dropdownTitle}>{title}</span>}
        {children}
      </div>
    </div>
  );
};
